/**
 * The view-layer switches: what is stored, what each row toggles, and the
 * panel widget they feed.
 *
 * Reads the closure it was cut out of through `LayerPrefsDeps`. `layerPref`
 * and `layerRows` are owned here; every other seam that needs a stored value
 * or the row list reaches it through the handle rather than a bare name.
 */

import { simApi } from '../../sim/api.ts';
import { createViewLayers, type ViewLayerRow } from '../viewLayers.ts';
import type { AtlasLive } from '../../atlas';
import type { AtmosphereViewHandle } from '../atmosphereView';
import type { BodySceneHandle } from '../bodyScene';
import type { HexViewHandle } from '../hexView';
import type { OrreryLayer } from '../orrery';
import type { PyramidViewHandle } from '../pyramidView';

export interface LayerPrefsDeps {
  readonly ARTIFICIAL_FILL_DEFAULT: boolean;
  readonly SOL_LIVE_DEFAULT: boolean;
  readonly overlay: HTMLElement;
  readonly own: (fn: () => void) => void;
  readonly pyramidView: PyramidViewHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly bodyScene: BodySceneHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly live: AtlasLive;
  /** Told the Layers list opened or closed, so the GROUND panel can
   *  auto-minimize where the list leaves it no room. */
  readonly onLayersOpenChange: (open: boolean) => void;
}

export interface LayerPrefsHandle {
  /** The row list `layerRows` is no longer a bare atlas.ts name for; every
   *  outside reader of the panel's rows goes through this instead. */
  rows(): readonly ViewLayerRow[];
  /** A stored preference by its row id, undefined if never set. */
  get(key: string): boolean | undefined;
}

export function createLayerPrefs(deps: LayerPrefsDeps): LayerPrefsHandle {
  const { ARTIFICIAL_FILL_DEFAULT, SOL_LIVE_DEFAULT, overlay, own } = deps;
  const { setArtificialLayer } = deps.pyramidView;
  const { setHexesEnabled } = deps.hexViewSeam;

  /**
   * The view's own visual layers, as data.
   *
   * Each row is an id, a face and the pair of functions that reach whatever
   * draws it, so a layer built later is one more row here and nothing else.
   * The orrery rows compose with the band gates the ladder drives every frame
   * rather than replacing them (`setLayerVisible`).
   */
  const LAYER_KEY = 'atlas.layers.v1';
  const orreryLayer = (id: OrreryLayer, label: string, fallback = true): ViewLayerRow => ({
    id, label,
    get: () => layerPref[id] ?? fallback,
    set: (on) => {
      layerPref[id] = on;
      writeLayerPrefs();
      if (!deps.hexViewSeam.layerInert) deps.bodyScene.state.orrery?.setLayerVisible(id, on);
    },
  });
  const layerPref: Record<string, boolean> = {};
  try {
    const stored = localStorage.getItem(LAYER_KEY);
    if (stored !== null) {
      const parsed: unknown = JSON.parse(stored);
      if (parsed && typeof parsed === 'object') {
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof v === 'boolean') layerPref[k] = v;
        }
      }
    }
  } catch { /* private mode, or a key some older build wrote */ }
  function writeLayerPrefs(): void {
    try { localStorage.setItem(LAYER_KEY, JSON.stringify(layerPref)); } catch { /* private mode */ }
  }
  const layerRows: readonly ViewLayerRow[] = [
    orreryLayer('orbitsHelio', 'ORBIT LINES'),
    orreryLayer('orbitsMoons', 'MOON ORBITS'),
    // Off by default: it REPLACES the moon rings above, and a ring is what a
    // pilot reads when parking.
    orreryLayer('spirals', 'MOON SPIRALS', false),
    orreryLayer('labels', 'BODY LABELS'),
    orreryLayer('moonLabels', 'MOON LABELS'),
    orreryLayer('rings', 'PLANET RINGS'),
    {
      id: 'ship',
      label: 'SHIP MARKER',
      get: () => layerPref['ship'] ?? true,
      set: (on) => {
        layerPref['ship'] = on;
        writeLayerPrefs();
        if (!deps.hexViewSeam.layerInert) deps.bodyScene.state.shipMarker?.setLayerVisible(on);
      },
    },
    // The bottom-right button this panel replaced, with its own older key.
    {
      id: 'hexes',
      label: 'HEX GRID',
      get: () => deps.hexViewSeam.hexesEnabled,
      set: (on) => setHexesEnabled(on),
    },
    {
      id: 'belts',
      label: 'ASTEROID BELTS',
      get: () => layerPref['belts'] ?? true,
      set: (on) => {
        layerPref['belts'] = on;
        writeLayerPrefs();
        if (!deps.hexViewSeam.layerInert) deps.bodyScene.state.belts?.setLayerVisible(on);
      },
    },
    {
      id: 'atmosphere',
      label: 'ATMOSPHERE',
      get: () => layerPref['atmosphere'] ?? true,
      set: (on) => {
        layerPref['atmosphere'] = on;
        writeLayerPrefs();
        if (!deps.hexViewSeam.layerInert) { deps.atmosphereView.atmoLayerOn = on; deps.live.needsRender = true; }
      },
    },
    {
      id: 'artificial',
      label: 'ARTIFICIAL FILL',
      get: () => layerPref['artificial'] ?? ARTIFICIAL_FILL_DEFAULT,
      set: (on) => {
        layerPref['artificial'] = on;
        writeLayerPrefs();
        if (!deps.hexViewSeam.layerInert) { setArtificialLayer(on); deps.live.needsRender = true; }
      },
    },
    {
      id: 'solLive',
      label: 'LIVE SUN',
      get: () => layerPref['solLive'] ?? SOL_LIVE_DEFAULT,
      set: (on) => {
        layerPref['solLive'] = on;
        writeLayerPrefs();
        if (!deps.hexViewSeam.layerInert) { deps.bodyScene.state.orrery?.setSolLive(on); deps.live.needsRender = true; }
      },
    },
    // No layerPref of its own: the pilot panel's TRAJECTORY button and this
    // row are two controls on simApi.trajectory's one state, not a copy of it.
    {
      id: 'trajectory',
      label: 'TRAJECTORY',
      get: () => simApi.trajectory.enabled(),
      set: (on) => { if (!deps.hexViewSeam.layerInert) simApi.trajectory.setEnabled(on); },
    },
  ];
  deps.hexViewSeam.viewLayers = createViewLayers({
    host: overlay.querySelector('#atlas-vl-host') as HTMLElement,
    rows: () => layerRows,
    onOpenChange: deps.onLayersOpenChange,
  });
  own(() => { deps.hexViewSeam.viewLayers?.dispose(); deps.hexViewSeam.viewLayers = null; });
  // The marker existed before the panel that switches it; the orrery's own rows
  // are applied where the registry lands, for the same reason.
  deps.bodyScene.state.shipMarker?.setLayerVisible(layerPref['ship'] ?? true);
  deps.bodyScene.state.belts?.setLayerVisible(layerPref['belts'] ?? true);
  deps.atmosphereView.atmoLayerOn = layerPref['atmosphere'] ?? true;

  return {
    rows: () => layerRows,
    get: (key: string): boolean | undefined => layerPref[key],
  };
}
